// Complete EJSTemplateEditor.js with all improvements
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSampleData } from './SampleDataContext';
import { Space, Typography, Radio, Tooltip, Button, Modal } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    CodeOutlined,
    FileTextOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    ExpandOutlined
} from '@ant-design/icons';
import SampleDataViewer from './SampleDataViewer';
import { evaluateEjsTemplate, getEjsExpressionContext } from './EJSUtils';
import { registerEjsLanguages } from './MonacoLanguageDefinitions';
import { registerEjsCompletionProviders } from './MonacoCompletionProviders';
import Editor from '@monaco-editor/react';
import HtmlRenderer from './HtmlRenderer';

const { Text } = Typography;

/**
 * EJSTemplateEditor component for editing EJS templates
 * Similar to JavaScriptExpressionEditor but specialized for EJS templates
 */
const EJSTemplateEditor = ({
                               value,
                               onChange,
                               multiline = false,
                               height = multiline ? 400 : 36,
                               placeholder,
                               evaluateOnChange = false,
                               label = null,
                               mode = 'text', // 'text' or 'html'
                               onModeChange = null,
                               showModeToggle = multiline, // Only show mode toggle for multiline by default
                               showSampleData = false,
                               ultraCompact = false, // Ultra-compact mode with inline icons
                           }) => {
    const { currentSample } = useSampleData();
    const [template, setTemplate] = useState(value || '');
    const [evaluationResult, setEvaluationResult] = useState(null);
    const [evaluationError, setEvaluationError] = useState(null);
    const [editorMode, setEditorMode] = useState(mode);
    const [shouldShowUndefined, setShouldShowUndefined] = useState(false);
    const [sampleDataVisible, setSampleDataVisible] = useState(showSampleData);
    const [outputVisible, setOutputVisible] = useState(false);
    const [outputInModal, setOutputInModal] = useState(false);

    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const sampleDataRef = useRef(currentSample);
    const completionDisposablesRef = useRef([]);
    const componentIdRef = useRef(`ejs-editor-${Math.random().toString(36).substr(2, 9)}`);

    // Custom styles for ultra-compact mode
    const ultraCompactStyles = ultraCompact ? {
        editorContainer: {
            position: 'relative',
        },
        indicatorContainer: {
            position: 'absolute',
            right: 8,
            top: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            pointerEvents: 'none', // Let clicks pass through to editor
            zIndex: 5,
        },
        indicator: {
            fontSize: 14,
            pointerEvents: 'auto', // Make icon clickable
            opacity: 0.8,
            padding: 2,
            cursor: 'pointer',
        },
        editor: {
            paddingRight: 60, // Make room for indicators
        }
    } : {};

    // Default placeholders based on mode and multiline
    const defaultPlaceholders = {
        text: {
            single: "Hello <%= record.agentName %>",
            multi: "Call ID: <%= record.id %>\nAgent: <%= record.agentName %>\nCall Score: <%= record.qualScore %>"
        },
        html: {
            single: "<span>Hello <%= record.agentName %></span>",
            multi: "<div>\n  <p>Call ID: <%= record.id %></p>\n  <p>Agent: <%= record.agentName %></p>\n  <p>Call Score: <%= record.qualScore %></p>\n</div>"
        }
    };

    // If no placeholder is provided, use the default based on mode and multiline
    const effectivePlaceholder = placeholder ||
        (multiline ? defaultPlaceholders[editorMode].multi : defaultPlaceholders[editorMode].single);

    // Update sample data ref when it changes and re-evaluate template
    useEffect(() => {
        sampleDataRef.current = currentSample;
        // Only re-evaluate if we have sample data and template
        if (template && currentSample) {
            try {
                evaluateTemplate(template);
            } catch (e) {
                console.error("Error evaluating with updated sample:", e);
            }
        }
    }, [currentSample]);

    // Get editor options including ultra-compact mode adjustments
    const getEditorOptions = () => {
        return {
            lineNumbers: multiline ? 'on' : 'off',
            scrollBeyondLastLine: false,
            minimap: { enabled: false },
            wordWrap: 'on',
            automaticLayout: true,
            quickSuggestions: { other: true, comments: true, strings: true },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            fontSize: 14,
            lineHeight: 22,
            padding: {
                top: 6,
                bottom: 6,
                right: ultraCompact ? 60 : 6,
                left: 6
            },
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
            renderLineHighlight: 'none',
            suggest: {
                maxVisibleSuggestions: 15,
                showValues: false,
            },
            contextmenu: false,
            renderIndentGuides: multiline,
            folding: multiline,
            selectionHighlight: false,
            occurrencesHighlight: false,
            // Customize selection color
            theme: 'ejs-theme-custom'
        };
    };

    // Mode change handler
    const handleModeChange = (newMode) => {
        setEditorMode(newMode);
        if (onModeChange) onModeChange(newMode);

        // Only update content if the editor is empty
        if (editorRef.current && (!template || template.trim() === '')) {
            const newPlaceholder = multiline
                ? defaultPlaceholders[newMode].multi
                : defaultPlaceholders[newMode].single;

            editorRef.current.setValue(newPlaceholder);
            setTemplate(newPlaceholder);
            if (onChange) onChange(newPlaceholder);

            // Evaluate if we have sample data
            if (currentSample) {
                setTimeout(() => {
                    try {
                        evaluateTemplate(newPlaceholder);
                    } catch (e) {
                        console.error("Error evaluating template after mode change:", e);
                    }
                }, 100);
            }
        } else {
            // Re-evaluate current content with new mode
            setTimeout(() => {
                try {
                    evaluateTemplate(template);
                } catch (e) {
                    console.error("Error evaluating template after mode change:", e);
                }
            }, 100);
        }
    };

    const insertTextAtCursor = useCallback(
        (text) => {
            if (!editorRef.current) return;
            const editor = editorRef.current;
            const selection = editor.getSelection();

            if (selection) {
                const range = {
                    startLineNumber: selection.startLineNumber,
                    startColumn: selection.startColumn,
                    endLineNumber: selection.endLineNumber,
                    endColumn: selection.endColumn,
                };

                // Get the model and text around cursor
                const model = editor.getModel();
                const lineContent = model.getLineContent(selection.startLineNumber);

                // Use our utility to check if we're inside an EJS expression
                const context = getEjsExpressionContext(
                    lineContent,
                    selection.startColumn - 1
                );

                // Format the text appropriately
                let insertText = text;

                // Only wrap with <%= %> if we're outside an EJS tag
                if (!context.insideExpression) {
                    insertText = `<%= ${text} %>`;
                }

                editor.executeEdits('insert-text', [{ range, text: insertText, forceMoveMarkers: true }]);
                editor.focus();

                const newValue = editor.getValue();
                setTemplate(newValue);

                if (onChange) onChange(newValue);
                if (evaluateOnChange && currentSample) {
                    evaluateTemplate(newValue);
                }
            }
        },
        [onChange, evaluateOnChange, currentSample]
    );

    // Evaluate EJS template using our utility
    const evaluateTemplate = useCallback(
        (templateToEval = template) => {
            // Reset showUndefined flag
            setShouldShowUndefined(false);

            // Early returns for invalid inputs
            if (!templateToEval || typeof templateToEval !== 'string' || !templateToEval.trim()) {
                setEvaluationResult(null);
                setEvaluationError(null);
                return;
            }

            if (!currentSample) {
                setEvaluationResult(null);
                setEvaluationError("No sample data available");
                return;
            }

            try {
                // Use the EJS utility to evaluate the template
                const result = evaluateEjsTemplate(templateToEval, currentSample);

                // Check for undefined or null results
                if (result === 'undefined') {
                    setShouldShowUndefined(true);
                }

                setEvaluationResult(result);
                setEvaluationError(null);
            } catch (error) {
                console.error("Template evaluation error:", error);
                setEvaluationResult(null);
                setEvaluationError(error.message || "Unknown error");
            }
        },
        [template, currentSample]
    );

    const handleTemplateChange = (newValue) => {
        setTemplate(newValue);
        if (onChange) onChange(newValue);

        // Evaluate on change
        if (currentSample) {
            if (window.evalTimeout) clearTimeout(window.evalTimeout);
            window.evalTimeout = setTimeout(() => {
                try {
                    evaluateTemplate(newValue);
                } catch (e) {
                    console.error("Error during delayed evaluation:", e);
                }
            }, 300);
        }
    };

    // Helper function to format property values for display
    const formatPropertyDetail = (value) => {
        const valueType = typeof value;
        if (valueType === 'string') {
            return `string: "${value.length > 20 ? value.substring(0, 20) + '...' : value}"`;
        } else if (valueType === 'number') {
            return `number: ${value}`;
        } else if (valueType === 'boolean') {
            return `boolean: ${value}`;
        } else if (valueType === 'object' && value !== null) {
            if (Array.isArray(value)) {
                return `array[${value.length}]`;
            } else {
                const objKeys = Object.keys(value);
                return `object: {${objKeys.slice(0, 3).join(', ')}${objKeys.length > 3 ? ', ...' : ''}}`;
            }
        }
        return valueType;
    };

    // Simple editor mount function
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;

        if (!window._ejsLanguagesRegistered) {
            registerEjsLanguages(monaco);

            // Add theme definition
            monaco.editor.defineTheme('ejs-theme', {
                base: 'vs',
                inherit: true,
                rules: [
                    { token: 'record', foreground: '0000ff', fontStyle: 'bold' },      // Blue bold for record
                    { token: 'record.property', foreground: '0000ff' },                // Blue for record properties
                    { token: 'workflow', foreground: '7c00ff', fontStyle: 'bold' },    // Purple bold for workflow
                    { token: 'delimiter.ejs', foreground: '008000', fontStyle: 'bold' }  // Green bold for EJS delimiters
                ],
                colors: {}
            });

            // Custom theme with different selection highlight
            monaco.editor.defineTheme('ejs-theme-custom', {
                base: 'vs',
                inherit: true,
                rules: [
                    { token: 'record', foreground: '0000ff', fontStyle: 'bold' },      // Blue bold for record
                    { token: 'record.property', foreground: '0000ff' },                // Blue for record properties
                    { token: 'workflow', foreground: '7c00ff', fontStyle: 'bold' },    // Purple bold for workflow
                    { token: 'delimiter.ejs', foreground: '008000', fontStyle: 'bold' }  // Green bold for EJS delimiters
                ],
                colors: {
                    'editor.selectionBackground': '#FFFACD',      // Light yellow (LemonChiffon)
                    'editor.selectionHighlightBackground': '#FFFACD80', // Transparent light yellow
                    'editor.inactiveSelectionBackground': '#FFFACD60',  // More transparent yellow
                }
            });

            window._ejsLanguagesRegistered = true;
        }

        // Always set the theme for this editor
        monaco.editor.setTheme('ejs-theme-custom');

        // Apply default value if editor is empty
        if (!editor.getValue() && effectivePlaceholder) {
            editor.setValue(effectivePlaceholder);
            setTemplate(effectivePlaceholder);
        }
        setTimeout(() => editor.focus(), 100);

        // Clean up existing completion providers attached to this component instance
        if (completionDisposablesRef.current.length > 0) {
            completionDisposablesRef.current.forEach(d => {
                try { d.dispose(); } catch(e) { console.error('Failed to dispose completion provider:', e); }
            });
            completionDisposablesRef.current = [];
        }

        // Determine language based on mode
        const language = editorMode === 'html' ? 'html-ejs' : 'text-ejs';

        // Register EJS languages if needed (this only happens once)
        if (!window._ejsLanguagesRegistered) {
            registerEjsLanguages(monaco);
            window._ejsLanguagesRegistered = true;
        }

        // Define a unique key for this language's providers
        const providersKey = `${language}-providers`;

        // Clean up any existing providers for this language
        if (window[providersKey]) {
            console.log(`Disposing existing providers for ${language}`);
            window[providersKey].forEach(d => {
                try { d.dispose(); } catch(e) { /* ignore errors */ }
            });
        }

        // Register new providers
        const disposables = registerEjsCompletionProviders(monaco, editor, {
            sampleDataRef,
            formatPropertyDetail
        });

        // Store globally by language and locally
        window[providersKey] = disposables;
        completionDisposablesRef.current = disposables;

        // Set the model language
        monaco.editor.setModelLanguage(editor.getModel(), language);

        // Initial evaluation
        if (currentSample) {
            try {
                evaluateTemplate(editor.getValue());
            } catch (e) {
                console.error("Error during initial evaluation:", e);
            }
        }

        console.log(`[${componentIdRef.current}] Editor mounted with ${disposables.length} completion providers`);
    };

    // Cleanup on unmount - consolidated single useEffect instead of duplicate ones
    useEffect(() => {
        // Component mount message
        console.log(`[${componentIdRef.current}] EJSTemplateEditor mounted`);

        // Use a static counter to track instances
        if (!EJSTemplateEditor.instanceCount) {
            EJSTemplateEditor.instanceCount = 0;
        }
        EJSTemplateEditor.instanceCount++;

        // Remember current language for cleanup
        const currentLanguage = editorMode === 'html' ? 'html-ejs' : 'text-ejs';
        const providersKey = `${currentLanguage}-providers`;

        return () => {
            // Decrement instance counter
            EJSTemplateEditor.instanceCount--;

            console.log(`[${componentIdRef.current}] EJSTemplateEditor unmounting. Remaining instances: ${EJSTemplateEditor.instanceCount}`);

            // Clear our local completion provider references
            if (completionDisposablesRef.current.length > 0) {
                completionDisposablesRef.current = [];
            }

            // If this is the last instance, clean up all global providers
            if (EJSTemplateEditor.instanceCount === 0) {
                // Clean up all language providers
                ['html-ejs-providers', 'text-ejs-providers'].forEach(key => {
                    if (window[key] && Array.isArray(window[key])) {
                        console.log(`Cleaning up global providers for ${key}`);
                        window[key].forEach(d => {
                            try { d.dispose(); } catch(e) { /* ignore */ }
                        });
                        window[key] = [];
                    }
                });
            }

            console.log(`[${componentIdRef.current}] EJSTemplateEditor unmounted`);
        };
    }, [editorMode]);

    // Update editor when the component value changes
    useEffect(() => {
        if (value !== undefined && value !== template) {
            setTemplate(value);
        }
    }, [value]);

    // Toggle sample data visibility
    const toggleSampleData = () => {
        setSampleDataVisible(!sampleDataVisible);
    };

    // Toggle output visibility
    const toggleOutput = () => {
        setOutputVisible(!outputVisible);
    };

    // Render compact evaluation result for single line editor
    const renderCompactEvaluationResult = () => {
        if (evaluationError) {
            return (
                <Space size="small">
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    <Text type="danger" style={{ fontSize: '12px' }}>
                        {evaluationError.length > 60 ? evaluationError.substring(0, 60) + '...' : evaluationError}
                    </Text>
                </Space>
            );
        }

        if (evaluationResult !== null || shouldShowUndefined) {
            return (
                <Space size="small">
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text style={{ fontSize: '12px' }}>
                        {shouldShowUndefined ? 'undefined' :
                            (evaluationResult && evaluationResult.length > 60 ?
                                evaluationResult.substring(0, 60) + '...' : evaluationResult)}
                    </Text>
                </Space>
            );
        }

        return null;
    };

    // Render ultra-compact indicators inside the editor
    const renderUltraCompactIndicators = () => {
        if (!ultraCompact) return null;

        const showStatusIcon = evaluationResult !== null || evaluationError !== null;

        return (
            <div style={ultraCompactStyles.indicatorContainer}>
                {showStatusIcon && (
                    <Tooltip title={evaluationError || `Sample Output: ${evaluationResult || 'undefined'}`}>
                        {evaluationError ?
                            <CloseCircleOutlined style={{ ...ultraCompactStyles.indicator, color: '#ff4d4f' }} /> :
                            <CheckCircleOutlined style={{ ...ultraCompactStyles.indicator, color: '#52c41a' }} />
                        }
                    </Tooltip>
                )}
                <Tooltip title={sampleDataVisible ? "Hide sample data" : "Show sample data"}>
                    {sampleDataVisible ?
                        <EyeInvisibleOutlined
                            style={{ ...ultraCompactStyles.indicator, color: '#1890ff' }}
                            onClick={toggleSampleData}
                        /> :
                        <EyeOutlined
                            style={{ ...ultraCompactStyles.indicator, color: '#1890ff' }}
                            onClick={toggleSampleData}
                        />
                    }
                </Tooltip>
            </div>
        );
    };

    return (
        <div className="ejs-template-editor">
            <Space direction="vertical" style={{ width: '100%' }}>
                {label && <Text strong>{label}</Text>}
                <div>
                    {/* Editor component */}
                    <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: 2,
                        backgroundColor: '#ffffff',
                        overflow: 'visible',
                        position: 'relative',
                        ...ultraCompactStyles.editorContainer
                    }}>
                        <Editor
                            height={height}
                            language={editorMode === 'html' ? 'html-ejs' : 'text-ejs'}
                            theme="vs-light"
                            value={template}
                            options={getEditorOptions()}
                            onChange={handleTemplateChange}
                            onMount={handleEditorDidMount}
                        />
                        {ultraCompact && renderUltraCompactIndicators()}
                    </div>

                    {/* Bottom row with evaluation result and mode toggle - only if not in ultra-compact mode */}
                    {!ultraCompact && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 4
                        }}>
                            {/* Left side - evaluation result */}
                            <div>
                                {!multiline && renderCompactEvaluationResult()}
                                {multiline && (
                                    <>
                                        {evaluationError ? (
                                            <Space size="small">
                                                <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                                <Text type="danger" style={{ fontSize: '12px' }}>
                                                    {evaluationError.length > 60 ? evaluationError.substring(0, 60) + '...' : evaluationError}
                                                </Text>
                                            </Space>
                                        ) : (evaluationResult !== null || shouldShowUndefined) ? (
                                            <Space size="small">
                                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() => setOutputVisible(!outputVisible)}
                                                    style={{ padding: '0 4px', height: 'auto' }}
                                                >
                                                    {outputVisible ? 'Hide Sample Output' : 'Show Sample Output'}
                                                </Button>
                                            </Space>
                                        ) : null}
                                    </>
                                )}
                            </div>

                            {/* Right side - toggles and controls */}
                            <Space size="small">
                                {/* Sample data toggle button */}
                                <Tooltip title={sampleDataVisible ? "Hide sample data" : "Show sample data"}>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={sampleDataVisible ?
                                            <EyeInvisibleOutlined style={{ color: '#1890ff' }} /> :
                                            <EyeOutlined style={{ color: '#1890ff' }} />
                                        }
                                        onClick={toggleSampleData}
                                    />
                                </Tooltip>

                                {/* Mode toggle (only if showModeToggle is true) */}
                                {showModeToggle && (
                                    <Radio.Group
                                        value={editorMode}
                                        onChange={(e) => handleModeChange(e.target.value)}
                                        optionType="button"
                                        buttonStyle="solid"
                                        size="small"
                                    >
                                        <Radio.Button value="text">
                                            <FileTextOutlined /> Text
                                        </Radio.Button>
                                        <Radio.Button value="html">
                                            <CodeOutlined /> HTML
                                        </Radio.Button>
                                    </Radio.Group>
                                )}
                            </Space>
                        </div>
                    )}
                </div>

                {/* HTML Output for multiline editors */}
                {multiline && outputVisible && evaluationResult !== null && !evaluationError && (
                    <div style={{ marginTop: 8, position: 'relative' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 4
                        }}>
                            <Text strong>Sample Output</Text>
                            <Button
                                type="text"
                                size="small"
                                icon={<ExpandOutlined />}
                                onClick={() => setOutputInModal(true)}
                                title="View in popup window"
                            />
                        </div>
                        <HtmlRenderer
                            html={evaluationResult}
                            height={150}
                            title="EJS Template Preview"
                        />
                    </div>
                )}

                {sampleDataVisible && (
                    <SampleDataViewer
                        onInsertText={insertTextAtCursor}
                        onExecuteTest={() => {
                            if (sampleDataRef.current) {
                                try {
                                    evaluateTemplate(template);
                                } catch (e) {
                                    console.error("Error executing test:", e);
                                    setEvaluationError(e.message);
                                }
                            } else {
                                setEvaluationError("No sample data available");
                            }
                        }}
                    />
                )}
            </Space>

            {/* Modal for detached output view */}
            <Modal
                title="Template Output"
                open={outputInModal}
                onCancel={() => setOutputInModal(false)}
                footer={null}
                width={700}
                bodyStyle={{ padding: 0 }}
            >
                <HtmlRenderer
                    html={evaluationResult || ''}
                    height={400}
                    width="100%"
                    title="EJS Template Preview"
                />
            </Modal>
        </div>
    );
};

export default EJSTemplateEditor;
